import React, { useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import useTPRMDialog from "./useTPRMDialog";

const QUESTION_TYPES = [
  { value: "yes_no", label: "Yes / No" },
  { value: "yes_no_partial", label: "Yes / No / Partial" },
  { value: "single_choice", label: "Single choice" },
  { value: "multiple_choice", label: "Multiple choice" },
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "evidence", label: "Evidence" },
];

const CHOICE_TYPES = new Set(["single_choice", "multiple_choice"]);
const inputClassName =
  "mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:disabled:bg-slate-800";
const textAreaClassName =
  "mt-1 min-h-24 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:disabled:bg-slate-800";

const parseDomainList = (value) =>
  [...new Set(value.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean))];

const questionForEditor = (question) => ({
  question_id: question.question_id || "",
  question_text: question.question_text || "",
  question_type: question.question_type || "",
  required: question.required !== false,
  evidence_required: Boolean(question.evidence_required),
  risk_domain: question.risk_domain || question.domain || "",
  weight: question.weight ?? 1,
  allowed_answers: Array.isArray(question.allowed_answers)
    ? question.allowed_answers
    : [],
  show_if_question_id: question.show_if?.question_id || "",
  show_if_equals: question.show_if?.equals ?? "",
  scoring_config: {
    ...(question.scoring_config || {}),
    answer_risk: { ...(question.scoring_config?.answer_risk || {}) },
  },
  answer_risk_text: scoringText(question.scoring_config),
});

const initialForm = (template, isCreate) => ({
  name: template?.name || "",
  description: template?.description || "",
  framework: template?.framework || "",
  version: template?.version || "1.0",
  is_active: template?.is_active ?? true,
  tenant_id: template?.tenant_id || "",
  risk_domains: Array.isArray(template?.risk_domains)
    ? template.risk_domains
    : [],
  scoring_policy_version: template?.scoring_policy_version || "tprm-risk-v2",
  questions: (template?.questions || []).map(questionForEditor),
  isCreate,
});

const newQuestion = (questions) => {
  let index = questions.length + 1;
  let questionId = `question-${index}`;
  while (questions.some((question) => question.question_id === questionId)) {
    index += 1;
    questionId = `question-${index}`;
  }
  return questionForEditor({
    question_id: questionId,
    question_text: "",
    question_type: "yes_no",
    required: true,
    evidence_required: false,
    weight: 1,
    allowed_answers: [],
    scoring_config: {},
    answer_risk_text: "",
  });
};

const scoringText = (config) =>
  Object.entries(config?.answer_risk || {})
    .map(([answer, score]) => `${answer}=${score}`)
    .join("\n");

const parseScoringText = (value) => {
  const answerRisk = Object.create(null);
  const errors = [];
  value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line, index) => {
      const separator = line.lastIndexOf("=");
      if (separator <= 0) {
        errors.push(`Scoring line ${index + 1} must use answer=score format.`);
        return;
      }
      const answer = line.slice(0, separator).trim().toLowerCase();
      const score = Number(line.slice(separator + 1).trim());
      if (!answer || !Number.isFinite(score) || score < 0 || score > 100) {
        errors.push(`Scoring line ${index + 1} needs an answer and a score from 0 to 100.`);
        return;
      }
      if (Object.prototype.hasOwnProperty.call(answerRisk, answer)) {
        errors.push(`Scoring answer "${answer}" is duplicated.`);
        return;
      }
      answerRisk[answer] = score;
    });
  return { answerRisk, errors };
};

const validateAndBuildPayload = (form, domainsText, customerAdmins) => {
  const errors = {};
  const domains = parseDomainList(domainsText);
  if (!form.name.trim()) errors.name = "Questionnaire name is required.";
  const customerAdminIds = new Set(customerAdmins.map((admin) => String(admin.id)));
  if (!form.tenant_id || !customerAdminIds.has(String(form.tenant_id))) {
    errors.tenant_id = "Select an existing customer administrator.";
  }
  const ids = new Set();
  const questions = form.questions.map((question, index) => {
    const prefix = `questions.${index}`;
    const id = question.question_id.trim();
    if (!id) errors[`${prefix}.question_id`] = "Question ID is required.";
    else if (ids.has(id)) {
      errors[`${prefix}.question_id`] = "Question IDs must be unique.";
    }
    ids.add(id);

    if (!question.question_text.trim()) {
      errors[`${prefix}.question_text`] = "Question text is required.";
    }
    if (!QUESTION_TYPES.some(({ value }) => value === question.question_type)) {
      errors[`${prefix}.question_type`] = "Select a supported question type.";
    }
    const weight = Number(question.weight);
    if (!Number.isFinite(weight) || weight <= 0) {
      errors[`${prefix}.weight`] = "Weight must be a number greater than zero.";
    }
    if (question.risk_domain && !domains.includes(question.risk_domain)) {
      errors[`${prefix}.risk_domain`] =
        "Choose a risk domain listed for this questionnaire.";
    }
    if (question.show_if_question_id) {
      if (
        !form.questions.slice(0, index).some(
          (candidate) => candidate.question_id === question.show_if_question_id,
        )
      ) {
        errors[`${prefix}.show_if_question_id`] =
          "Choose a previous question as the condition.";
      }
      if (!String(question.show_if_equals).trim()) {
        errors[`${prefix}.show_if_equals`] =
          "Enter the answer that makes this question visible.";
      }
    }

    const allowedAnswers = (question.allowed_answers || [])
      .map((answer) => String(answer).trim())
      .filter(Boolean);
    if (CHOICE_TYPES.has(question.question_type) && !allowedAnswers.length) {
      errors[`${prefix}.allowed_answers`] =
        "At least one allowed answer is required for choice questions.";
    }
    const normalizedOptions = allowedAnswers.map((answer) => answer.toLowerCase());
    if (new Set(normalizedOptions).size !== normalizedOptions.length) {
      errors[`${prefix}.allowed_answers`] = "Allowed answers must be unique.";
    }

    const scoreText =
      question.answer_risk_text ?? scoringText(question.scoring_config);
    const { answerRisk, errors: scoringErrors } = parseScoringText(scoreText);
    if (scoringErrors.length) {
      errors[`${prefix}.scoring_config`] = scoringErrors.join(" ");
    }
    const scoringConfig = {};
    if (Object.keys(answerRisk).length) {
      scoringConfig.answer_risk = answerRisk;
    }
    const defaultRisk = question.scoring_config?.default_risk;
    if (defaultRisk !== undefined && defaultRisk !== null && defaultRisk !== "") {
      const score = Number(defaultRisk);
      if (!Number.isFinite(score) || score < 0 || score > 100) {
        errors[`${prefix}.default_risk`] =
          "Default risk must be a number between 0 and 100.";
      } else {
        scoringConfig.default_risk = score;
      }
    }
    if (
      ["text", "date", "evidence"].includes(question.question_type)
      && !Object.prototype.hasOwnProperty.call(scoringConfig, "default_risk")
    ) {
      errors[`${prefix}.default_risk`] =
        "A default risk score is required for this answer type.";
    }
    return {
      question_id: id,
      question_text: question.question_text.trim(),
      question_type: question.question_type,
      required: Boolean(question.required),
      evidence_required: Boolean(question.evidence_required),
      risk_domain: question.risk_domain || "",
      weight,
      ...(question.show_if_question_id
        ? {
            show_if: {
              question_id: question.show_if_question_id,
              equals: String(question.show_if_equals).trim(),
            },
          }
        : {}),
      ...(CHOICE_TYPES.has(question.question_type)
        ? { allowed_answers: allowedAnswers }
        : {}),
      ...(Object.keys(scoringConfig).length ? { scoring_config: scoringConfig } : {}),
    };
  });

  if (Object.keys(errors).length) return { errors };
  const payload = {
    name: form.name.trim(),
    description: form.description.trim(),
    framework: form.framework.trim(),
    version: form.version.trim(),
    is_active: Boolean(form.is_active),
    risk_domains: domains,
    scoring_policy_version: form.scoring_policy_version.trim(),
    questions,
  };
  if (form.isCreate) payload.tenant_id = form.tenant_id.trim();
  return { payload, errors };
};

const QuestionnaireEditorModal = ({
  template,
  isCreate = false,
  readOnly = false,
  onClose,
  onSave,
  saving = false,
  error = "",
  customerAdmins = [],
  customerAdminsLoading = false,
  customerAdminsError = "",
  onRetryCustomerAdmins,
  returnFocusRef,
}) => {
  const dialogRef = useTPRMDialog({
    onClose,
    closeOnEscape: !saving,
    returnFocusRef,
  });
  const [form, setForm] = useState(() => initialForm(template, isCreate));
  const [domainsText, setDomainsText] = useState(
    () => (template?.risk_domains || []).join("\n"),
  );
  const [validationErrors, setValidationErrors] = useState({});
  const domains = useMemo(() => parseDomainList(domainsText), [domainsText]);

  const updateForm = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));

  const updateQuestion = (index, name, value) => {
    setForm((current) => ({
      ...current,
      questions: current.questions.map((question, questionIndex) =>
        questionIndex === index ? { ...question, [name]: value } : question,
      ),
    }));
  };

  const submit = (event) => {
    event.preventDefault();
    const result = validateAndBuildPayload(form, domainsText, customerAdmins);
    setValidationErrors(result.errors);
    if (!result.payload) return;
    onSave(result.payload);
  };

  const moveQuestion = (index, offset) => {
    setForm((current) => {
      const destination = index + offset;
      if (destination < 0 || destination >= current.questions.length) {
        return current;
      }
      const questions = [...current.questions];
      [questions[index], questions[destination]] = [
        questions[destination],
        questions[index],
      ];
      return { ...current, questions };
    });
  };

  const addQuestion = () =>
    setForm((current) => ({
      ...current,
      questions: [...current.questions, newQuestion(current.questions)],
    }));

  const removeQuestion = (index) =>
    setForm((current) => ({
      ...current,
      questions: current.questions.filter(
        (_question, questionIndex) => questionIndex !== index,
      ),
    }));

  const fieldErrorId = (key) =>
    `questionnaire-field-error-${key.replace(/\./g, "-")}`;

  const fieldError = (key) =>
    validationErrors[key] ? (
      <span
        className="mt-1 block text-xs text-rose-700 dark:text-rose-300"
        id={fieldErrorId(key)}
      >
        {validationErrors[key]}
      </span>
    ) : null;

  const labelClass =
    "block text-sm font-medium text-slate-700 dark:text-slate-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-3 sm:p-5">
      <section
        aria-labelledby="questionnaire-editor-title"
        aria-modal="true"
        className="my-auto max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:p-6"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2
              className="text-xl font-semibold text-slate-900 dark:text-white"
              id="questionnaire-editor-title"
            >
              {readOnly
                ? "View questionnaire"
                : isCreate
                  ? "Create questionnaire"
                  : "Edit questionnaire"}
            </h2>
            {template?.template_version && (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Questionnaire version {template.template_version}
              </p>
            )}
          </div>
          <button
            aria-label="Close questionnaire editor"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            disabled={saving}
            onClick={onClose}
            title="Close questionnaire editor"
            type="button"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-6" onSubmit={submit}>
          {error && (
            <p
              className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
              role="alert"
            >
              {error}
            </p>
          )}

          <fieldset className="grid grid-cols-1 gap-4 md:grid-cols-2" disabled={readOnly || saving}>
            <label className={labelClass}>
              Questionnaire name
              <input
                aria-label="Questionnaire name"
                aria-invalid={Boolean(validationErrors.name)}
                aria-describedby={
                  validationErrors.name ? fieldErrorId("name") : undefined
                }
                className={inputClassName}
                onChange={(event) => updateForm("name", event.target.value)}
                value={form.name}
              />
              {fieldError("name")}
            </label>
            <div className={labelClass}>
              <label htmlFor="customer-admin-selector">
                Customer administrator
              </label>
              <select
                aria-label="Customer administrator"
                aria-invalid={Boolean(validationErrors.tenant_id)}
                aria-describedby={
                  validationErrors.tenant_id
                    ? fieldErrorId("tenant_id")
                    : undefined
                }
                className={inputClassName}
                disabled={
                  !isCreate ||
                  readOnly ||
                  saving ||
                  customerAdminsLoading ||
                  Boolean(customerAdminsError) ||
                  customerAdmins.length === 0
                }
                id="customer-admin-selector"
                onChange={(event) => updateForm("tenant_id", event.target.value)}
                required={isCreate}
                value={form.tenant_id}
              >
                <option value="">
                  {customerAdminsLoading
                    ? "Loading customer administrators..."
                    : customerAdmins.length
                      ? "Select a customer administrator"
                      : "No customer administrators available"}
                </option>
                {customerAdmins.map((admin) => (
                  <option key={admin.id} value={String(admin.id)}>
                    {admin.name || "Unnamed Customer Admin"} -{" "}
                    {admin.company_name || "Company not specified"}
                  </option>
                ))}
                {!isCreate &&
                  form.tenant_id &&
                  !customerAdmins.some(
                    (admin) => String(admin.id) === String(form.tenant_id),
                  ) && (
                    <option value={String(form.tenant_id)}>
                      Current customer administrator unavailable -{" "}
                      {String(form.tenant_id)}
                    </option>
                  )}
              </select>
              {!isCreate && !readOnly && (
                <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                  Template ownership cannot be changed.
                </span>
              )}
              {customerAdminsError && (
                <span className="mt-1 block text-xs text-rose-700 dark:text-rose-300" role="alert">
                  {customerAdminsError}
                </span>
              )}
              {customerAdminsError && onRetryCustomerAdmins && (
                <button
                  className="mt-2 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200"
                  onClick={onRetryCustomerAdmins}
                  type="button"
                >
                  Retry customer administrator list
                </button>
              )}
              {!customerAdminsLoading &&
                !customerAdminsError &&
                customerAdmins.length === 0 && (
                  <span className="mt-1 block text-xs text-slate-600 dark:text-slate-400">
                    No customer administrators were found.
                  </span>
                )}
              {fieldError("tenant_id")}
            </div>
            <label className={labelClass}>
              Framework
              <input
                aria-label="Questionnaire framework"
                className={inputClassName}
                onChange={(event) =>
                  updateForm("framework", event.target.value)
                }
                value={form.framework}
              />
            </label>
            <label className={labelClass}>
              Questionnaire version label
              <input
                aria-label="Questionnaire version label"
                className={inputClassName}
                onChange={(event) => updateForm("version", event.target.value)}
                value={form.version}
              />
            </label>
            <label className={labelClass}>
              Scoring policy version
              <input
                aria-label="Scoring policy version"
                className={inputClassName}
                onChange={(event) =>
                  updateForm("scoring_policy_version", event.target.value)
                }
                value={form.scoring_policy_version}
              />
            </label>
            <label className={`${labelClass} flex items-center gap-2`}>
              <input
                aria-label="Questionnaire active"
                checked={form.is_active}
                onChange={(event) =>
                  updateForm("is_active", event.target.checked)
                }
                type="checkbox"
              />
              Active and selectable for new assessments
            </label>
            <label className={`${labelClass} md:col-span-2`}>
              Description
              <textarea
                aria-label="Questionnaire description"
                className={textAreaClassName}
                onChange={(event) =>
                  updateForm("description", event.target.value)
                }
                value={form.description}
              />
            </label>
            <label className={`${labelClass} md:col-span-2`}>
              Risk domains (one per line)
              <textarea
                aria-label="Questionnaire risk domains"
                className={textAreaClassName}
                onChange={(event) => setDomainsText(event.target.value)}
                placeholder={"Access Control\nData Protection"}
                value={domainsText}
              />
            </label>
          </fieldset>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  Questions ({form.questions.length})
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Question order is stored in the questionnaire’s ordered list.
                </p>
              </div>
              {!readOnly && (
                <button
                  className="rounded-lg border border-blue-200 bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-blue-900 dark:focus:ring-offset-slate-900"
                  disabled={saving}
                  onClick={addQuestion}
                  title="Add question"
                  type="button"
                >
                  Add question
                </button>
              )}
            </div>

            {form.questions.length === 0 && (
              <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-400">
                No questions have been added.
              </p>
            )}

            {form.questions.map((question, index) => {
              const choiceQuestion = CHOICE_TYPES.has(question.question_type);
              const questionPrefix = `questions.${index}`;
              return (
                <fieldset
                  className="space-y-4 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                  disabled={readOnly || saving}
                  key={`${question.question_id}-${index}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <legend className="font-medium text-slate-900 dark:text-white">
                      Question {index + 1}
                    </legend>
                    {!readOnly && (
                      <div className="flex gap-2">
                        <button
                          aria-label={`Move question ${index + 1} up`}
                          className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40 dark:border-slate-700"
                          disabled={index === 0 || saving}
                          onClick={() => moveQuestion(index, -1)}
                          type="button"
                        >
                          Move up
                        </button>
                        <button
                          aria-label={`Move question ${index + 1} down`}
                          className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40 dark:border-slate-700"
                          disabled={index === form.questions.length - 1 || saving}
                          onClick={() => moveQuestion(index, 1)}
                          type="button"
                        >
                          Move down
                        </button>
                        <button
                          aria-label={`Remove question ${index + 1}`}
                          className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 dark:border-rose-900 dark:text-rose-300"
                          onClick={() => removeQuestion(index)}
                          type="button"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className={labelClass}>
                      Question ID
                      <input
                        aria-label={`Question ${index + 1} ID`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.question_id`],
                        )}
                        aria-describedby={
                          validationErrors[`${questionPrefix}.question_id`]
                            ? fieldErrorId(`${questionPrefix}.question_id`)
                            : undefined
                        }
                        className={inputClassName}
                        onChange={(event) =>
                          updateQuestion(index, "question_id", event.target.value)
                        }
                        value={question.question_id}
                      />
                      {fieldError(`${questionPrefix}.question_id`)}
                    </label>
                    <label className={labelClass}>
                      Question type
                      <select
                        aria-label={`Question ${index + 1} type`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.question_type`],
                        )}
                        aria-describedby={
                          validationErrors[`${questionPrefix}.question_type`]
                            ? fieldErrorId(`${questionPrefix}.question_type`)
                            : undefined
                        }
                        className={inputClassName}
                        onChange={(event) =>
                          updateQuestion(
                            index,
                            "question_type",
                            event.target.value,
                          )
                        }
                        value={question.question_type}
                      >
                        <option value="">Select a supported type</option>
                        {question.question_type &&
                          !QUESTION_TYPES.some(
                            ({ value }) => value === question.question_type,
                          ) && (
                            <option disabled value={question.question_type}>
                              Unsupported current type — choose a supported type
                            </option>
                          )}
                        {QUESTION_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      {fieldError(`${questionPrefix}.question_type`)}
                    </label>
                    <label className={`${labelClass} md:col-span-2`}>
                      Question text
                      <textarea
                        aria-label={`Question ${index + 1} text`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.question_text`],
                        )}
                        aria-describedby={
                          validationErrors[`${questionPrefix}.question_text`]
                            ? fieldErrorId(`${questionPrefix}.question_text`)
                            : undefined
                        }
                        className={textAreaClassName}
                        onChange={(event) =>
                          updateQuestion(
                            index,
                            "question_text",
                            event.target.value,
                          )
                        }
                        value={question.question_text}
                      />
                      {fieldError(`${questionPrefix}.question_text`)}
                    </label>
                    <label className={labelClass}>
                      Show only when previous answer matches
                      <select
                        aria-label={`Question ${index + 1} condition question`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.show_if_question_id`],
                        )}
                        className={inputClassName}
                        onChange={(event) =>
                          updateQuestion(
                            index,
                            "show_if_question_id",
                            event.target.value,
                          )
                        }
                        value={question.show_if_question_id}
                      >
                        <option value="">Always show</option>
                        {form.questions.slice(0, index).map((candidate) => (
                          <option
                            key={candidate.question_id}
                            value={candidate.question_id}
                          >
                            {candidate.question_id}: {candidate.question_text || "Untitled"}
                          </option>
                        ))}
                      </select>
                      {fieldError(`${questionPrefix}.show_if_question_id`)}
                    </label>
                    {question.show_if_question_id && (
                      <label className={labelClass}>
                        Matching answer
                        <input
                          aria-label={`Question ${index + 1} matching answer`}
                          aria-invalid={Boolean(
                            validationErrors[`${questionPrefix}.show_if_equals`],
                          )}
                          className={inputClassName}
                          onChange={(event) =>
                            updateQuestion(
                              index,
                              "show_if_equals",
                              event.target.value,
                            )
                          }
                          value={question.show_if_equals}
                        />
                        {fieldError(`${questionPrefix}.show_if_equals`)}
                      </label>
                    )}
                    {choiceQuestion && (
                      <label className={`${labelClass} md:col-span-2`}>
                        Allowed answers (one per line)
                        <textarea
                          aria-label={`Question ${index + 1} allowed answers`}
                          aria-invalid={Boolean(
                            validationErrors[`${questionPrefix}.allowed_answers`],
                          )}
                          aria-describedby={
                            validationErrors[`${questionPrefix}.allowed_answers`]
                              ? fieldErrorId(`${questionPrefix}.allowed_answers`)
                              : undefined
                          }
                          className={textAreaClassName}
                          onChange={(event) =>
                            updateQuestion(
                              index,
                              "allowed_answers",
                              event.target.value.split(/\r?\n/),
                            )
                          }
                          value={(question.allowed_answers || []).join("\n")}
                        />
                        {fieldError(`${questionPrefix}.allowed_answers`)}
                      </label>
                    )}
                    <label className={labelClass}>
                      Risk domain
                      <select
                        aria-label={`Question ${index + 1} risk domain`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.risk_domain`],
                        )}
                        aria-describedby={
                          validationErrors[`${questionPrefix}.risk_domain`]
                            ? fieldErrorId(`${questionPrefix}.risk_domain`)
                            : undefined
                        }
                        className={inputClassName}
                        onChange={(event) =>
                          updateQuestion(
                            index,
                            "risk_domain",
                            event.target.value,
                          )
                        }
                        value={question.risk_domain}
                      >
                        <option value="">No domain</option>
                        {domains.map((domain) => (
                          <option key={domain} value={domain}>
                            {domain}
                          </option>
                        ))}
                      </select>
                      {fieldError(`${questionPrefix}.risk_domain`)}
                    </label>
                    <label className={labelClass}>
                      Scoring weight
                      <input
                        aria-label={`Question ${index + 1} weight`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.weight`],
                        )}
                        aria-describedby={
                          validationErrors[`${questionPrefix}.weight`]
                            ? fieldErrorId(`${questionPrefix}.weight`)
                            : undefined
                        }
                        className={inputClassName}
                        min="0.01"
                        onChange={(event) =>
                          updateQuestion(index, "weight", event.target.value)
                        }
                        step="any"
                        type="number"
                        value={question.weight}
                      />
                      {fieldError(`${questionPrefix}.weight`)}
                    </label>
                    <label className={`${labelClass} md:col-span-2`}>
                      Answer risk mapping (optional; one answer=score per line)
                      <textarea
                        aria-label={`Question ${index + 1} answer risk mapping`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.scoring_config`],
                        )}
                        aria-describedby={
                          validationErrors[`${questionPrefix}.scoring_config`]
                            ? fieldErrorId(`${questionPrefix}.scoring_config`)
                            : undefined
                        }
                        className={textAreaClassName}
                        onChange={(event) =>
                          updateQuestion(
                            index,
                            "answer_risk_text",
                            event.target.value,
                          )
                        }
                        placeholder={"yes=0\nno=100"}
                        value={
                          question.answer_risk_text ??
                          scoringText(question.scoring_config)
                        }
                      />
                      {fieldError(`${questionPrefix}.scoring_config`)}
                    </label>
                    <label className={labelClass}>
                      Default risk score (0–100
                      {["text", "date", "evidence"].includes(question.question_type)
                        ? ", required"
                        : ", optional"})
                      <input
                        aria-label={`Question ${index + 1} default risk`}
                        aria-invalid={Boolean(
                          validationErrors[`${questionPrefix}.default_risk`],
                        )}
                        aria-describedby={
                          validationErrors[`${questionPrefix}.default_risk`]
                            ? fieldErrorId(`${questionPrefix}.default_risk`)
                            : undefined
                        }
                        className={inputClassName}
                        max="100"
                        min="0"
                        onChange={(event) =>
                          updateQuestion(index, "scoring_config", {
                            ...question.scoring_config,
                            default_risk: event.target.value,
                          })
                        }
                        step="any"
                        type="number"
                        value={question.scoring_config?.default_risk ?? ""}
                      />
                      {fieldError(`${questionPrefix}.default_risk`)}
                    </label>
                    <div className="flex flex-wrap gap-6">
                      <label className={`${labelClass} flex items-center gap-2`}>
                        <input
                          aria-label={`Question ${index + 1} required`}
                          checked={question.required}
                          onChange={(event) =>
                            updateQuestion(
                              index,
                              "required",
                              event.target.checked,
                            )
                          }
                          type="checkbox"
                        />
                        Required
                      </label>
                      <label className={`${labelClass} flex items-center gap-2`}>
                        <input
                          aria-label={`Question ${index + 1} evidence required`}
                          checked={question.evidence_required}
                          onChange={(event) =>
                            updateQuestion(
                              index,
                              "evidence_required",
                              event.target.checked,
                            )
                          }
                          type="checkbox"
                        />
                        Evidence required
                      </label>
                    </div>
                  </div>
                </fieldset>
              );
            })}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
            <button
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
              disabled={saving}
              onClick={onClose}
              type="button"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
                disabled={
                  saving ||
                  customerAdminsLoading ||
                  Boolean(customerAdminsError) ||
                  customerAdmins.length === 0 ||
                  !customerAdmins.some(
                    (admin) => String(admin.id) === String(form.tenant_id),
                  )
                }
                type="submit"
              >
                {saving ? "Saving..." : "Save questionnaire"}
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
};

export default QuestionnaireEditorModal;
